import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Typography,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  OutlinedInput,
  Chip,
  Modal,
  Stack,
  IconButton,
  ListSubheader,
  TextField,
} from "@mui/material";
import { course as courseConst } from "../../course/constants";
import Button from "../../../components/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "@mui/material/styles";
import { useIRGrade } from "../hooks";
import { toast as toastFn } from "react-toastify";
import { uploadJson } from "../../../store/IRGrading/IRGradingBaseApi";
import { useCourse } from "../../course/hooks";
import AddIcon from "@mui/icons-material/AddCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { generateUUID, json_files } from "../../../utils";
import { Plus, Swap, X } from "@phosphor-icons/react";
import { makeStyles } from "tss-react/mui";
import Loader from "../../../components/Loader";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 300,
    },
  },
};

const useStyles = makeStyles()((theme, args) => ({
  root: {
    padding: "0px 20px",
    [theme.breakpoints.down("md")]: {
      padding: "0px",
    },
  },
  headerText: {
    margin: "0",
    fontWeight: "700",
    lineHeight: "1.5",
    fontSize: "2rem",
  },
  gridHead: {
    padding: "8px 10px !important",
    display: "flex",
    justifyContent: "space-between",
    background: theme.palette.primary.blue,
    borderRadius: "5px",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  gridInputLabel: {
    color: theme.palette.common.white,
    fontSize: "22px",
    fontWeight: "500",
  },
  addLvlIcon: {
    padding: theme.spacing(0),
    color: theme.palette.common.white,
    minWidth: "30px",
  },
  accordianSummary: {
    background: "var(--mui-palette-gray-lightGray50)",
    boxShadow: "none",
    borderRadius: "4px",
    minHeight: "48px !important",
    "& .Mui-expanded": {
      margin: "12px 0 !important",
    },
  },
  accordianHead: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  levelTxt: {
    color: theme.palette.primary.blue,
    width: "80%",
    fontWeight: "bold",
  },
  chipBox: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  courseChip: {
    color: "var(--mui-palette-primary-blue)",
    "& .MuiChip-deleteIcon": {
      color: "var(--mui-palette-error-light)",
      "&:hover": {
        color: "var(--mui-palette-error-main)",
      },
    },
  },
  gradeBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  gradeTxt: {
    backgroundColor: theme.palette.primary.orange,
    padding: theme.spacing(1),
    textAlign: "center",
    color: "#fff",
    borderRadius: theme.spacing(1),
    boxShadow: theme.spacing(1),
    fontSize: "1rem",
  },
  modalBox: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[24],
    padding: theme.spacing(4),
  },
  modalClose: {
    position: "absolute",
    top: 8,
    right: 8,
  },
}));

export const Edit = () => {
  const theme = useTheme();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { loadCoursesList, courseList } = useCourse();
  const {
    loadCourseMappingV2,
    courseMappingListV2,
    loadAllGrades,
    grades,
    loadLevels,
    levelsList,
    addLevel,
    deleteLevel,
    loading,
  } = useIRGrade();
  const [levels, setLevels] = useState([]);
  const [courses, setCourses] = useState();
  // `selectOpen` is keyed by `${levelId}::${studentType}` so the URBAN and
  // RURAL dropdowns at the same level open / close independently.
  const [selectOpen, setSelectOpen] = useState({});
  const [openLvlModal, setOpenLvlModal] = useState({});
  // Per-dropdown search text, keyed by `${levelId}::${studentType}` so each
  // course dropdown filters independently.
  const [courseSearch, setCourseSearch] = useState({});

  // Course name/studentType come from the enriched mapping (`courseMappingListV2`,
  // the pristine server response — kept separate from the mutable `levels` so the
  // enrichment survives edits). Already-mapped courses are excluded from the course
  // list, so this is the only source that can resolve them.
  const enrichedById = useMemo(() => {
    const map = {};
    (courseMappingListV2 || []).forEach((level) => {
      (level.subLevels || []).forEach((sub) => {
        if (sub?.irCourseId) {
          map[sub.irCourseId] = {
            courseName: sub.courseName,
            studentType: sub.studentType,
          };
        }
      });
    });
    return map;
  }, [courseMappingListV2]);

  // Combined lookup for display: enriched mapping overlaid with the course list
  // (the list is authoritative for freshly-selectable/unmapped courses).
  const courseById = useMemo(() => {
    const map = { ...enrichedById };
    (courses || []).forEach((c) => {
      map[c.id] = c;
    });
    return map;
  }, [enrichedById, courses]);

  // Lookup helper — returns the studentType of a course id, defaulting to
  // URBAN for missing/legacy rows so untagged content stays visible.
  const getCourseStudentType = (courseId) => {
    const c = courseById[courseId];
    return (c?.studentType || "URBAN").toUpperCase();
  };

  // Courses filtered to a specific studentType — feeds each row's dropdown.
  const coursesForType = (type) =>
    (courses || []).filter(
      (c) => (c.studentType || "URBAN").toUpperCase() === type,
    );

  useEffect(() => {
    loadCoursesList({ pageSize: 50, category: "Course" });
    loadCourseMappingV2();
    loadLevels();
    loadAllGrades({});
  }, []);

  const handleOpenLvlModal = (levelId) => {
    setOpenLvlModal((prev) => ({ ...prev, [levelId]: true }));
  };

  const handleCloseLvlModal = (levelId) => {
    setOpenLvlModal((prev) => ({ ...prev, [levelId]: false }));
  };

  const handleAddLvl = async (e, level) => {
    const name = level;
    const value = parseInt(name.slice(1), 10);
    const gradeId = e.target.value;
    let payload = { name, value, gradeId };
    await addLevel(payload);
  };

  const clearLvl = async (level) => {
    const currentLvl = levelsList?.find((l) => l.name === level);
    let _id = currentLvl?.id;
    await deleteLevel({ id: _id });
  };

  const findGradeName = (level) => {
    const gradeId = levelsList.find((l) => l.name === level)?.gradeId;
    const gradeName = grades?.data?.find((g) => g.id === gradeId)?.grade;
    return gradeName;
  };

  useEffect(() => {
    if (courseList) {
      setCourses(courseList);
    }
    if (courseMappingListV2) {
      setLevels(courseMappingListV2);
    }
  }, [courseList, courseMappingListV2, levelsList]);

  const handleCourseChange = (levelId, forType, event) => {
    const { value } = event.target;
    setLevels((prevLevels) => {
      return prevLevels.map((level) => {
        if (level.levelId === levelId) {
          // Keep OTHER-type sub-levels untouched so editing one row never
          // wipes out the other type at the same level.
          const otherTypeSubs = (level.subLevels || []).filter(
            (sub) => getCourseStudentType(sub.irCourseId) !== forType,
          );
          // Rebuild THIS type's sub-levels from the new selection.
          // `order` numbers are scoped per studentType (URBAN: 1, 2…; RURAL: 1, 2…
          // independently). The backend filter screens by type + order together
          // so overlap is fine.
          const activeTypeSubs = value.map((courseId, index) => ({
            irCourseId: courseId,
            order: index + 1,
            level: `${level.irLevel}.${index + 1}`,
          }));
          return { ...level, subLevels: [...otherTypeSubs, ...activeTypeSubs] };
        }
        return level;
      });
    });
    setSelectOpen((prev) => ({ ...prev, [`${levelId}::${forType}`]: false }));
  };

  const handleDeleteChip = (levelId, forType, chipToDelete) => {
    setLevels((prevLevels) => {
      return prevLevels.map((level) => {
        if (level.levelId === levelId) {
          const otherTypeSubs = (level.subLevels || []).filter(
            (sub) => getCourseStudentType(sub.irCourseId) !== forType,
          );
          const activeTypeSubs = (level.subLevels || [])
            .filter(
              (sub) =>
                getCourseStudentType(sub.irCourseId) === forType &&
                sub.irCourseId !== chipToDelete,
            )
            .map((sub, index) => ({
              ...sub,
              order: index + 1,
              level: `${level.irLevel}.${index + 1}`,
            }));
          return { ...level, subLevels: [...otherTypeSubs, ...activeTypeSubs] };
        }
        return level;
      });
    });
  };

  const handelAddLevel = () => {
    let newLevelNumber;
    let newLevelId = `irl-${generateUUID()}`;
    if (levels.length === 0) {
      newLevelNumber = 1; // Start with L1 if no levels exist
    } else {
      // Find last level (e.g., L12 → L13)
      const lastLevel = levels[levels.length - 1].irLevel;
      const lastLevelNumber = parseInt(lastLevel.replace("L", ""), 10);
      newLevelNumber = lastLevelNumber + 1;
    }
    const newLevel = {
      irLevel: `L${newLevelNumber}`,
      subLevels: [],
      levelId: newLevelId,
      value: newLevelNumber,
    };
    setLevels((prevLevels) => [...prevLevels, newLevel]);
  };

  const handleDeleteLevel = (levelId) => {
    setLevels((prevLevels) =>
      prevLevels.filter((level) => level.levelId !== levelId),
    );
  };

  const showToast = (msg, type) => {
    if (!type) {
      return toastFn(msg);
    }
    toastFn[type](msg);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // `levels` carries display-only `courseName`/`studentType` appended by the
    // enrichment API — strip them so the canonical JSON keeps storing only ids
    // (names are re-derived server-side on each fetch and never go stale).
    const sendData = levels.map((level) => ({
      irLevel: level.irLevel,
      levelId: level.levelId,
      value: level.value,
      subLevels: (level.subLevels || []).map((sub) => ({
        irCourseId: sub.irCourseId,
        order: sub.order,
        level: sub.level,
      })),
    }));
    const json = JSON.stringify(sendData, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const formData = new FormData();
    formData.append("file", blob, json_files.irLevels.name);
    try {
      const resp = await uploadJson({ formData });
      if (resp.message) {
        showToast("Course Mapping Update Success", "success");
        navigate("/ir-grading");
      }
    } catch (e) {
      return showToast(e.error, "error");
    }
  };

  return (
    <Box className={classes.root}>
      {loading ? (
        <Loader />
      ) : (
        <div className="question header-container">
          <div className="header">
            <Typography
              align="left"
              variant="h1"
              color="primary.blue"
              className={classes.headerText}
            >
              {"Course Mapping"}
            </Typography>
          </div>
          <Grid container alignItems="center">
            <Grid item xs={12} className={classes.gridHead}>
              <InputLabel className={classes.gridInputLabel}>
                Intelliread Levels
              </InputLabel>
              <Button
                className={classes.addLvlIcon}
                onClick={() => handelAddLevel()}
                placeholder={
                  <Link
                    title="Add Level"
                    sx={{ color: "var(--mui-palette-primary-contrastText)" }}
                  >
                    <AddIcon />{" "}
                  </Link>
                }
                variant="text"
              />
            </Grid>
          </Grid>
          <form onSubmit={handleSubmit}>
            {levels.map((level, index) => {
              const gradeId = levelsList.find(
                (l) => l.name === level.irLevel,
              )?.gradeId;
              return (
                <Accordion key={level?.levelId} defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                    className={classes.accordianSummary}
                  >
                    <Box className={classes.accordianHead}>
                      <Box className={classes.levelTxt}>{level.irLevel}</Box>
                      {parseInt(level.irLevel.replace("L", ""), 10) > 12 && (
                        <Button
                          sx={{ p: 0 }}
                          disabled={
                            parseInt(level.irLevel.replace("L", ""), 10) <= 12
                          }
                          onClick={() => handleDeleteLevel(level.levelId)}
                          variant="text"
                          placeholder={
                            <Link title="Delete Level">
                              <DeleteIcon
                                sx={{
                                  color: "var(--mui-palette-error-main)",
                                  fontSize: "1.5em",
                                }}
                              />{" "}
                            </Link>
                          }
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Two rows per level — Urban on top, Rural below. Each
                        row is independent: its dropdown is scoped to that
                        type, its chips show only that type's mapped courses,
                        and editing one never touches the other. */}
                    {Object.keys(courseConst.STUDENT_TYPE).map((typeKey) => {
                      // Classify a sub-level by its own enriched studentType first
                      // (carried on the mapping from the backend), then by the
                      // course lookup, defaulting untagged content to URBAN.
                      const subType = (sub) =>
                        (
                          sub.studentType ||
                          courseById[sub.irCourseId]?.studentType ||
                          "URBAN"
                        ).toUpperCase();
                      const visibleIds = (level.subLevels || [])
                        .filter((sub) => subType(sub) === typeKey)
                        .map((sub) => sub.irCourseId);
                      // Options list only the UNMAPPED courses of this type. Courses
                      // already mapped (to this or any level) are intentionally left
                      // out — they render as chips and are removed via the chip's X.
                      const typeCourses = coursesForType(typeKey);
                      const selectKey = `${level.levelId}::${typeKey}`;
                      const label = `Select ${courseConst.STUDENT_TYPE[typeKey]} Courses`;
                      return (
                        <FormControl
                          key={selectKey}
                          sx={{ mt: 2, width: "100%" }}
                        >
                          <InputLabel id={`select-label-${selectKey}`}>
                            {label}
                          </InputLabel>
                          <Select
                            labelId={`select-label-${selectKey}`}
                            multiple
                            value={visibleIds}
                            onChange={(e) =>
                              handleCourseChange(level?.levelId, typeKey, e)
                            }
                            input={<OutlinedInput label={label} />}
                            renderValue={(selected) => (
                              <Box className={classes.chipBox}>
                                {selected.length === 0 && (
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No {courseConst.STUDENT_TYPE[typeKey]}{" "}
                                    courses mapped yet.
                                  </Typography>
                                )}
                                {selected.map((value) => (
                                  <Chip
                                    key={value}
                                    label={
                                      courseById[value]?.courseName || value
                                    }
                                    onDelete={() =>
                                      handleDeleteChip(
                                        level?.levelId,
                                        typeKey,
                                        value,
                                      )
                                    }
                                    onMouseDown={(event) =>
                                      event.stopPropagation()
                                    }
                                    className={classes.courseChip}
                                    color={
                                      typeKey === "RURAL"
                                        ? "success"
                                        : "warning"
                                    }
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            )}
                            open={selectOpen[selectKey] ?? false}
                            onOpen={() =>
                              setSelectOpen((prev) => ({
                                ...prev,
                                [selectKey]: true,
                              }))
                            }
                            onClose={() => {
                              setSelectOpen((prev) => ({
                                ...prev,
                                [selectKey]: false,
                              }));
                              // Reset search so the next open starts empty.
                              setCourseSearch((prev) => ({
                                ...prev,
                                [selectKey]: "",
                              }));
                            }}
                            MenuProps={MenuProps}
                          >
                            <ListSubheader
                              sx={{ p: 1, bgcolor: "background.paper" }}
                            >
                              <TextField
                                size="small"
                                autoFocus
                                fullWidth
                                placeholder="Search courses"
                                value={courseSearch[selectKey] || ""}
                                onChange={(e) =>
                                  setCourseSearch((prev) => ({
                                    ...prev,
                                    [selectKey]: e.target.value,
                                  }))
                                }
                                onKeyDown={(e) => {
                                  // Keep MUI's built-in type-ahead from hijacking
                                  // keystrokes typed into the search box.
                                  if (e.key !== "Escape") {
                                    e.stopPropagation();
                                  }
                                }}
                              />
                            </ListSubheader>
                            {typeCourses.length === 0 && (
                              <MenuItem disabled value="">
                                No {courseConst.STUDENT_TYPE[typeKey]} courses
                                available. Create one in Courses with student
                                type = {courseConst.STUDENT_TYPE[typeKey]}.
                              </MenuItem>
                            )}
                            {typeCourses.map((course) => {
                              const search = (courseSearch[selectKey] || "")
                                .toLowerCase()
                                .trim();
                              // Hide (don't unmount) non-matching options so the
                              // multi-select keeps its checked state and MUI never
                              // warns about out-of-range values.
                              const hidden =
                                search &&
                                !course.courseName
                                  ?.toLowerCase()
                                  .includes(search);
                              return (
                                <MenuItem
                                  key={course.id}
                                  value={course.id}
                                  sx={{ display: hidden ? "none" : undefined }}
                                >
                                  {course.courseName}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      );
                    })}
                    <Box className={classes.gradeBox}>
                      {gradeId && (
                        <Stack direction="row" spacing={2}>
                          <Box className={classes.gradeTxt}>
                            {findGradeName(level.irLevel)}{" "}
                          </Box>
                        </Stack>
                      )}
                      {gradeId ? (
                        <Button
                          sx={{ mt: 1 }}
                          onClick={() => handleOpenLvlModal(level.levelId)}
                          variant="outlined"
                          placeholder={" Change Grade"}
                          startIcon={<Swap />}
                        />
                      ) : (
                        <Button
                          sx={{ mt: 1 }}
                          onClick={() => handleOpenLvlModal(level.levelId)}
                          variant="outlined"
                          placeholder={" Add Grade"}
                          startIcon={<Plus />}
                        />
                      )}
                    </Box>
                    <Modal
                      open={openLvlModal[level.levelId]}
                      onClose={() => handleCloseLvlModal(level.levelId)}
                      aria-labelledby="modal-modal-title"
                      aria-describedby="modal-modal-description"
                    >
                      <Box className={classes.modalBox}>
                        <IconButton
                          onClick={() => handleCloseLvlModal(level.levelId)}
                          className={classes.modalClose}
                        >
                          <X />
                        </IconButton>
                        <FormControl sx={{ mt: 2, width: "100%" }}>
                          <InputLabel id={`select-label-${level?.levelId}`}>
                            Select Grade
                          </InputLabel>
                          <Select
                            disabled={gradeId}
                            onChange={(e) => handleAddLvl(e, level.irLevel)}
                            labelId={`select-label-${level?.levelId}`}
                            value={
                              levelsList.find((l) => l.name === level.irLevel)
                                ?.gradeId || ""
                            } // Pre-select the grade based on level
                            input={<OutlinedInput label="Select Grade" />}
                          >
                            {grades?.data?.map((grade) => (
                              <MenuItem key={grade.id} value={grade.id}>
                                {grade.grade}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Box
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            sx={{ mt: 1 }}
                            onClick={() => clearLvl(level.irLevel)}
                            variant="contained"
                            placeholder={" Clear "}
                            disabled={!gradeId}
                          />
                        </Box>
                      </Box>
                    </Modal>
                  </AccordionDetails>
                </Accordion>
              );
            })}
            <Box sx={{ mt: 4 }}>
              <Button
                size="large"
                type="submit"
                variant="contained"
                color="primary"
                placeholder={"Save Changes"}
              />
            </Box>
          </form>
        </div>
      )}
    </Box>
  );
};
